import {
  Button, Dialog, DialogActions, DialogContent,
  DialogTitle, RadioGroup,
} from "@material-ui/core";
import React, { useState, useEffect, useMemo } from 'react';
import { useAptorApi } from "../../../../../Api";
import { useIntl } from "react-intl";
import moment from "moment";
import { TextField, DateField, TextAreaField } from "../../../../../Components/Field";
import { ComplianceReportCheckbox } from "../../../Components/Compliance/Checkbox";
import { TypedFormFieldValue } from "../../../../../Components/Form/Form.types";

interface Iprops {
  open: boolean;
  setOpen: (val: boolean) => void;
  rowData: any;
  onSaveSuccess?: () => void;
}

const EditObservationModal = ({ open, setOpen, rowData, onSaveSuccess }: Iprops) => {
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();

  const initialAnswer = useMemo<TypedFormFieldValue<string>>(
    () => ({ value: rowData?.action ?? '' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowData?.id],
  );

  const initialDeadline = useMemo<TypedFormFieldValue<Date> | undefined>(
    () => (rowData?.deadline ? { value: moment(rowData.deadline).toDate() } : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowData?.id],
  );

  const [compliance, setCompliance] = useState<'yes' | 'no' | 'deficiency'>(
    () => rowData?.compliance ?? null,
  );
  const [answer, setAnswer] = useState<TypedFormFieldValue<string> | undefined>(initialAnswer);
  const [deadline, setDeadline] = useState<TypedFormFieldValue<Date> | undefined>(initialDeadline);

  useEffect(() => {
    if (rowData && open) {
      setCompliance(rowData?.compliance ?? null);
      setAnswer({ value: rowData?.action ?? '' });
      setDeadline(rowData?.deadline ? { value: moment(rowData.deadline).toDate() } : undefined);
    }
  }, [rowData?.id, open]);

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    try {
      await api.updateObservation({
        ReportId: rowData?.reportId,
        QuestionId: rowData?.id,
        Compliance: compliance,
        Answer: rowData?.answer,
        Deadline: deadline?.value,
        Action: answer?.value,
        LegislationType: rowData?.legislationType,
        IsCustomQuestion: rowData?.isCustomQuestion,
      });
      onSaveSuccess?.();
      setOpen(false);
    } catch (error) {
      console.error('Error saving observation:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="observation-dialog-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '8px',
          padding: '0',
        },
      }}
    >
      <DialogTitle
        id="observation-dialog-title"
        disableTypography
        style={{ padding: '16px 24px 12px', borderBottom: '1px solid #e0e0e0' }}
      >
        <span style={{ fontSize: '16px', fontWeight: 500, color: '#1a1a1a' }}>
          {formatMessage({
            id: 'lawportal.evaluation-control.non-conformities.edit.modal.heading',
            defaultMessage: 'Handling of non conformity / observation',
          })}
        </span>
      </DialogTitle>

      <DialogContent style={{ padding: '16px 24px' }}>
        <p style={{ fontSize: '15px', margin: '0 0 16px' }}>
          {rowData?.number}, {rowData?.legislationName}
        </p>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '20px' }}>
          <div>
            {rowData?.compliance === 'deficiency' && (
              <ComplianceReportCheckbox type="deficiency" value={rowData?.compliance} hidden={false} style={{marginLeft:"-17px"}}  />
            )}
            {rowData?.compliance === 'no' && (
              <ComplianceReportCheckbox type="no" value={rowData?.compliance} hidden={false} />
            )}
          </div>
          <p style={{ fontSize: '14px', color: '#1a1a1a', margin: '0', lineHeight: 1.5 }}>
            {rowData?.answer}
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <TextAreaField
            key={`action-${rowData?.id}`}
            name="answer"
            label={formatMessage({
              id: 'law-portal.observation.required.action',
              defaultMessage: 'Enter required action',
            })}
            rows={4}
            multiline
            errorTexts={[]}
            initialState={initialAnswer} 
            onChange={setAnswer}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '70px' }}>
            <RadioGroup
              value={compliance}
              onChange={(e) => setCompliance(e.target.value as 'yes' | 'no' | 'deficiency')}
            >
              <ComplianceReportCheckbox type="yes" value={compliance} hidden={false} />
            </RadioGroup>
          </div>

          <div style={{ flex: 1, minWidth: '160px',marginBottom: '-8px' }}>
            {/* ✅ key=rowData.id ensures remount with correct initialState when row changes */}
          <DateField
            key={`deadline-${rowData?.id}`}
              name="deadline"
              errorTexts={[]}
              placeholder={formatMessage({
                id: 'law-portal.observation.date.action.placeholder',
                defaultMessage: 'Enter date of action',
              })}
              initialState={initialDeadline}  // ✅ useMemo value — always in sync
              onChange={setDeadline}
            />
          </div>

          <DialogActions
            style={{
              justifyContent: 'flex-end',
              justifyItems: 'center',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '-8px'
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              style={{
                border: '1.5px solid #e05a5a',
                color: '#e05a5a',
                fontWeight: 500,
                letterSpacing: '0.5px',
                minWidth: '80px',
              }}
            >
              {formatMessage({ id: 'form.cancel', defaultMessage: 'AVBRYT' })}
            </Button>
            <Button
              variant="outlined"
              onClick={handleSave}
              style={{
                border: '1.5px solid #e05a5a',
                color: '#e05a5a',
                fontWeight: 500,
                letterSpacing: '0.5px',
                minWidth: '80px',
              }}
            >
              {formatMessage({ id: 'form.save', defaultMessage: 'SPARA' })}
            </Button>
          </DialogActions>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditObservationModal;